import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormService } from 'src/app/general/services/form.service';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogService } from 'src/app/general/services/dialog.service';
import { BackendService } from '../../services/backend.service';
import { SessionService } from '../../services/session.service';
import { StorageService } from '../../services/storage.service';
import { TitleComponent } from 'src/app/general/components/title/title.component';
import { FieldContainerDirective } from 'src/app/general/directives/field/field-container.directive';
import { FieldDirective } from 'src/app/general/directives/field/field.directive';
import { Router } from '@angular/router';
import { ActionComponent } from 'src/app/general/components/action/action.component';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-sign-in></app-sign-in>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    TitleComponent,
    FieldContainerDirective,
    FieldDirective,
    ActionComponent
  ],
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.css', '../../../../styles.css']
})
export class SignInComponent {

  hidePassword: boolean;
  errorMessage: string;

  emailFormControl: FormControl;
  passwordFormControl: FormControl;
  rememberMeFormControl: FormControl;

  constructor(
      public formService: FormService,
      private dialogService: DialogService,
      private backendService: BackendService,
      private translateService: TranslateService,
      private sessionService: SessionService,
      private storageService: StorageService,
      private router: Router) {
    this.hidePassword = true;
    this.errorMessage = '';

    const lastUserEmail = this.storageService.getLastUserEmailStore().get();
    let emailInitialValue = '';
    let rememberMeInitialValue = false;
    if (lastUserEmail) {
      emailInitialValue = lastUserEmail;
      rememberMeInitialValue = true;
    }

    this.emailFormControl = new FormControl(emailInitialValue, [Validators.required, Validators.email]);
    this.passwordFormControl = new FormControl('', [Validators.required]);
    this.rememberMeFormControl = new FormControl(rememberMeInitialValue);
  }

  public signIn(): void {
    this.errorMessage = '';
    const closeDialog = this.dialogService.openProgressDialog();
    this.backendService.signIn(
      this.emailFormControl.value,
      this.passwordFormControl.value,
      /* onSuccess= */ response => {
        closeDialog();
        if (!response.isImpersonationAllowed) {
          this.translateService.get('error_impersonation_required').subscribe(text => this.errorMessage = text);
        } else {
          this.sessionService.setAuthToken(response.authToken, response.since, response.authTokenExpiration);
          this.sessionService.setIsAdminUser(response.isAdminUser);
          this.sessionService.setIsPrivilegedUser(response.isPrivilegedUser);
          this.sessionService.setIsSuperuser(response.isSuperuser);
          if (this.rememberMeFormControl.value) {
            this.storageService.getLastUserEmailStore().set(this.emailFormControl.value);
          } else {
            this.storageService.getLastUserEmailStore().clear();
          }
          this.router.navigate([''], {});
        }
      },
      /* onError= */ error => {
        closeDialog();
        if (error.isServerError()) {
          if (error.getServerErrorCode() === 'NOT_FOUND') {
            this.translateService.get('error_invalid_credentials').subscribe(text => this.errorMessage = text);
          } else if (error.getServerErrorCode() === 'USER_SUSPENDED_WAITING_FOR_EMAIL_VERIFICATION') {
            this.translateService.get('error_user_suspended_waiting_for_email_verification').subscribe(text => this.errorMessage = text);
          } else if (error.getServerErrorCode() === 'USER_SUSPENDED') {
            this.translateService.get('error_user_suspended').subscribe(text => this.errorMessage = text);
          } else if (error.getServerErrorCode() === 'INVALID_INPUT'){
            this.translateService.get('error_invalid_input').subscribe(text => this.errorMessage = text);
          } else {
            this.translateService.get('error_internal').subscribe(text => this.errorMessage = text);
          }
        } else {
          this.translateService.get('error_internal').subscribe(text => this.errorMessage = text);
        }
      });
  }
}
